import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, describeError } from "@/lib/api";

export default function ForgotPassword() {
  const [params] = useSearchParams();
  // Carried over from sign-in or sign-up, so it doesn't need typing twice.
  const [email, setEmail] = useState(() => params.get("email") || "");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  // Hidden-in-part email the link went to (only when a username was typed).
  const [sentTo, setSentTo] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setSentTo(data?.sent_to || "");
      setSent(true);
    } catch (err) {
      const msg = describeError(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md" data-testid="forgot-password-page">
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl font-black tracking-tighter">Forgot password</h1>
          <p className="text-muted-foreground mt-2">We'll email you a link to choose a new one.</p>
        </div>

        <div className="bg-surface border border-border rounded-2xl p-8 space-y-5">
          {sent ? (
            <div className="space-y-5" data-testid="forgot-password-sent">
              {sentTo ? (
                <p className="text-sm">
                  We've sent a reset link to <span className="font-semibold">{sentTo}</span>, the email on
                  that account. It expires in 1 hour. Check your spam folder if it doesn't arrive.
                </p>
              ) : (
                <>
                  <p className="text-sm">
                    If <span className="font-semibold">{email.trim()}</span> is the email on your Jangle account, a
                    reset link is on its way. It expires in 1 hour. Check your spam folder if it doesn't arrive.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Nothing after a few minutes? The email may be spelt differently on your account.{" "}
                    <button
                      type="button"
                      onClick={() => { setSent(false); setEmail(""); }}
                      className="text-foreground underline underline-offset-4"
                      data-testid="forgot-password-try-username"
                    >
                      Try your username instead
                    </button>
                    {" "}and we'll show you which email the link goes to.
                  </p>
                </>
              )}
              <Button asChild className="w-full h-11 rounded-full bg-brand hover:bg-brand-hover text-brand-foreground font-semibold">
                <Link to="/login">Back to sign in</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                  Email or username
                </Label>
                <Input
                  id="email"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-background h-11"
                  data-testid="forgot-password-email-input"
                />
              </div>

              {error && (
                <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-full bg-brand hover:bg-brand-hover text-brand-foreground font-semibold"
                data-testid="forgot-password-submit-button"
              >
                {loading ? "Sending…" : "Send reset link"}
              </Button>

              <div className="text-center text-sm text-muted-foreground pt-2">
                Remembered it?{" "}
                <Link to="/login" className="text-foreground underline-offset-4 hover:underline">
                  Sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
